import { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * Formly extension to automatically add data-cy attributes to all form fields for e2e testing.
 *
 * This extension:
 * - Adds data-cy attributes using the field's key or keyPath
 * - Handles nested fields with dot notation
 * - Works with repeating fields by adding indices
 * - Preserves existing data-cy attributes if manually set
 *
 * @param field - The Formly field configuration
 */
export function dataCyExtension(field: FormlyFieldConfig): void {
  // Initialize props if not present
  if (!field.props) {
    field.props = {};
  }

  // Initialize attributes if not present
  if (!field.props.attributes) {
    field.props.attributes = {};
  }

  // Skip if data-cy is already manually set
  if (field.props.attributes['data-cy']) {
    return;
  }

  // Generate data-cy value - try multiple sources
  let dataCyValue = '';

  // First try keyPath (most specific)
  if (field.props.keyPath) {
    dataCyValue = field.props.keyPath;
  }
  // Then try building from parent and key
  else if (field.key) {
    // If there's a parent field, build the full path
    if (field.parent && field.parent.props?.keyPath) {
      dataCyValue = `${field.parent.props.keyPath}.${field.key}`;
    } else {
      dataCyValue = String(field.key);
    }
    // Store the keyPath for child fields to use
    field.props.keyPath = dataCyValue;
  }
  // For fields without key (like structural fields), use type as fallback
  else if (field.type && typeof field.type === 'string') {
    dataCyValue = `field-type-${field.type}`;
  }

  if (dataCyValue) {
    field.props.attributes['data-cy'] = dataCyValue;
  }

  // For wrapper fields (tabs, groups, etc.), also add data-cy to the wrapper element
  if (field.wrappers && field.wrappers.length > 0) {
    if (!field.props.wrapperAttributes) {
      field.props.wrapperAttributes = {};
    }
    if (!field.props.wrapperAttributes['data-cy']) {
      field.props.wrapperAttributes['data-cy'] = `wrapper-${dataCyValue}`;
    }
  }

  // Handle fieldGroup recursively
  if (field.fieldGroup) {
    field.fieldGroup.forEach(childField => {
      if (field.fieldGroup) {
        // Recursively apply data-cy to child fields
        dataCyExtension(childField);
      } else {
        if (childField.props?.attributes == undefined) {
          childField.props.attributes = {};
        }
        childField.props.attributes['data-cy'] = childField.props.keyPath;
      }
    });
  }

  // Handle fieldArray for repeating fields
  if (field.fieldArray) {
    // Add data-cy to the add button if present
    if (!field.props.addButtonAttributes) {
      field.props.addButtonAttributes = {};
    }
    if (!field.props.addButtonAttributes['data-cy']) {
      field.props.addButtonAttributes['data-cy'] = `add-${dataCyValue}`;
    }

    // Add data-cy to the remove button template if present
    if (!field.props.removeButtonAttributes) {
      field.props.removeButtonAttributes = {};
    }
    if (!field.props.removeButtonAttributes['data-cy']) {
      field.props.removeButtonAttributes['data-cy'] = `remove-${dataCyValue}`;
    }
  }
}

/**
 * Extension configuration for registration
 */
export const dataCyExtensionConfig = {
  name: 'dataCy',
  extension: {
    prePopulate: (field: FormlyFieldConfig) => {
      // Run extension on field first
      dataCyExtension(field);

      // Ensure it runs on all nested fields
      if (field.fieldGroup) {
        field.fieldGroup.forEach(child => dataCyExtension(child));
      }
      // if (field.fieldArray) {
      //   dataCyExtension(field.fieldArray);
      // }
    },
    onPopulate: (field: FormlyFieldConfig) => {
      // Run again during populate phase to catch any dynamically added fields
      dataCyExtension(field);
    },
    postPopulate: (field: FormlyFieldConfig) => {
      // Final pass to ensure all fields have data-cy
      dataCyExtension(field);
    },
  },
};
